import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BrBox } from "@/components/BrBox";
import { BrBtn } from "@/components/BrBtn";
import { BrLabel } from "@/components/BrLabel";
import { BrTag } from "@/components/BrTag";
import { Logo } from "@/components/Logo";
import { useToast } from "@/components/ToastProvider";
import { ChessBoard } from "@/chess/board/ChessBoard";
import {
  coordsFromSquare,
  fenToBoard,
  legalDestinationsFrom,
  lineToSan,
  sideToMove,
  squareName,
  tryMove,
  uciSquares,
  uciToSan,
  useCompleteSession,
  useMarkItemSolved,
  useRefillEndless,
  useTrainingSession,
  offsetShadow,
  TYPE,
  type TrainingItemOut,
} from "@chess/shared";
import { useTheme } from "@/theme/ThemeProvider";
import { useViewport } from "@/util/viewport";

interface HistoryItem {
  n: number;
  ok: boolean;
  label: string;
  detail: string;
}

export function TrainingPlayPage() {
  const p = useTheme();
  const { isMobile, width } = useViewport();
  const nav = useNavigate();
  const params = useParams<{ sessionId: string }>();
  const sessionId = params.sessionId ?? null;
  const [search] = useSearchParams();
  const endless = search.get("endless") === "1";
  // When launched from a game's analysis ("Solve"), return there when done.
  const fromGameId = search.get("from");
  const doneDestination = fromGameId ? `/app/analysis/${fromGameId}` : "/app";
  const toast = useToast();
  const { data: session, isLoading, error } = useTrainingSession(sessionId);
  const markSolved = useMarkItemSolved();
  const refillEndless = useRefillEndless();
  const completeSession = useCompleteSession();
  // Routine sessions enforce their own clock/tries; the manual "on wrong move"
  // switch is shown ONLY for endless + analysis-fix sessions.
  const isRoutine = !!session?.routine_run_id;

  // Local item list so endless mode can append refilled batches.
  const [items, setItems] = useState<TrainingItemOut[]>([]);
  useEffect(() => {
    if (session?.items) setItems(session.items);
  }, [session?.items]);

  const [index, setIndex] = useState(0);
  const item = items[index];

  const startFen = item?.position_fen ?? null;
  const [boardFen, setBoardFen] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ row: number; col: number } | null>(null);
  const [candidates, setCandidates] = useState<{ row: number; col: number }[]>([]);
  // result === "fail" means the position is OVER without a solve (gave up or
  // "give up after first wrong" mode). A plain wrong guess does NOT set this —
  // the board stays live so the user can keep trying.
  const [result, setResult] = useState<"success" | "fail" | null>(null);
  const [hint, setHint] = useState(false);
  const [lineRevealed, setLineRevealed] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);
  const [history, setHistory] = useState<HistoryItem[]>([]);

  // Per-move clock + tries come from the item (routine settings); null clock
  // means untimed, null max_tries means infinite.
  const timeLimit = item?.time_limit_seconds ?? null;
  const maxTries = item?.max_tries ?? null;
  const [timeLeft, setTimeLeft] = useState(timeLimit ?? 0);
  const [triesUsed, setTriesUsed] = useState(0);
  const [startedAt, setStartedAt] = useState<number>(0);

  // Wrong moves tried on the CURRENT position (SAN), shown in a red table until
  // it's solved or abandoned.
  const [wrongAttempts, setWrongAttempts] = useState<string[]>([]);
  // The center "keep trying?" modal and the move that triggered it.
  const [modalMove, setModalMove] = useState<string | null>(null);
  // Once the user clicks "Continue", suppress the modal for THIS position.
  const [modalDismissed, setModalDismissed] = useState(false);
  // Guessing mode, persisted across positions/sessions.
  const [guessMode, setGuessMode] = useState<"modal" | "continue" | "giveup">(() => {
    const v = typeof localStorage !== "undefined" ? localStorage.getItem("trainGuessMode") : null;
    return v === "continue" || v === "giveup" ? v : "modal";
  });
  function changeGuessMode(m: "modal" | "continue" | "giveup") {
    setGuessMode(m);
    try {
      localStorage.setItem("trainGuessMode", m);
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    if (!startFen) return;
    setBoardFen(startFen);
    setSelected(null);
    setCandidates([]);
    setResult(null);
    setHint(false);
    setLineRevealed(false);
    setGaveUp(false);
    setTimeLeft(timeLimit ?? 0);
    setTriesUsed(0);
    setStartedAt(Date.now());
    setWrongAttempts([]);
    setModalMove(null);
    setModalDismissed(false);
  }, [startFen, index, timeLimit]);

  // Countdown only runs when this item has a per-move clock and the position
  // has actually been initialised (startedAt set by the reset effect) — this
  // avoids a transient timeLeft===0 firing a spurious timeout while the session
  // is still loading.
  useEffect(() => {
    if (timeLimit == null || result || gaveUp || startedAt === 0) return;
    if (timeLeft <= 0) {
      giveUp(true);
      return;
    }
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, result, gaveUp, timeLimit, startedAt]);

  const board = useMemo(() => (boardFen ? fenToBoard(boardFen) : Array.from({ length: 8 }, () => Array(8).fill(null))), [boardFen]);
  const playerColor: "w" | "b" = startFen ? sideToMove(startFen) : "w";

  // Endless mode hides the batch size — it should feel like an unbounded stream.
  // "POSITION 4" not "POSITION 4 OF 20".
  const solvedCount = history.filter((h) => h.ok).length;
  const posLabel = endless ? `POSITION ${index + 1}` : `POSITION ${index + 1} OF ${items.length}`;
  const posLabelPad = endless
    ? `POSITION ${String(index + 1).padStart(2, "0")}`
    : `POSITION ${String(index + 1).padStart(2, "0")} OF ${items.length}`;
  // A position can have several correct moves (multiPV) — best_moves holds them
  // all; the hint highlights the first one's destination square.
  const firstBestMove = ((item?.best_moves ?? [])[0] ?? "").slice(0, 4);
  const correctTo = firstBestMove.length >= 4 ? coordsFromSquare(firstBestMove.slice(2, 4) as `${"a"}1`) : null;

  // The bad move that was actually played — shown above the board and drawn as
  // an arrow on the board so the user sees what to avoid.
  const badUci = item?.played_move ?? null;
  const badSquares = badUci ? uciSquares(badUci) : null;
  const badMoveSan = badUci ? uciToSan(startFen, badUci) : null;
  const badArrowFrom = badSquares ? coordsFromSquare(badSquares.from) : null;
  const badArrowTo = badSquares ? coordsFromSquare(badSquares.to) : null;
  // All equally-good lines (multiPV), each as a SAN sequence, for the reveal.
  const bestLinesSan = item
    ? (item.best_lines ?? []).map((line) => lineToSan(startFen, line.join(" "))).filter(Boolean)
    : [];

  // Eval drop in standard White-POV (the universal eval-bar convention):
  // White worsening = numbers go down, Black worsening = numbers go up.
  const evalLabelWhite = (cp: number | null, mate: number | null): string | null => {
    if (mate != null && mate !== 0) {
      return `${mate > 0 ? "#" : "#-"}${Math.abs(mate)}`;
    }
    if (cp == null) return null;
    const v = cp / 100;
    return `${v >= 0 ? "+" : ""}${v.toFixed(1)}`;
  };
  const evalBeforeText = item ? evalLabelWhite(item.eval_before_cp, item.mate_before) : null;
  const evalAfterText = item ? evalLabelWhite(item.eval_after_cp, item.mate_after) : null;
  const evalDropText =
    evalBeforeText != null && evalAfterText != null && evalBeforeText !== evalAfterText
      ? `dropping eval from ${evalBeforeText} to ${evalAfterText}`
      : null;

  if (isLoading) {
    return <Centered p={p}>Loading session…</Centered>;
  }
  if (error) {
    return (
      <Centered p={p}>
        <div style={{ marginBottom: 12 }}>Session not ready yet.</div>
        <BrBtn variant="ghost" onClick={() => nav("/app/train")}>← Back</BrBtn>
      </Centered>
    );
  }
  if (!item) {
    return (
      <Centered p={p}>
        <div style={{ marginBottom: 12 }}>No positions in this session.</div>
        <BrBtn variant="ghost" onClick={() => nav("/app/train")}>← Back</BrBtn>
      </Centered>
    );
  }

  function handleSquareClick(row: number, col: number, piece: string | null) {
    if (result || !boardFen) return;
    if (!selected) {
      if (piece && ((playerColor === "w" && piece === piece.toUpperCase()) || (playerColor === "b" && piece === piece.toLowerCase()))) {
        const sq = squareName(row, col);
        const dests = legalDestinationsFrom(boardFen, sq);
        setSelected({ row, col });
        setCandidates(dests.map((d) => coordsFromSquare(d)));
      } else if (piece) {
        toast.push({ kind: "info", text: `You play ${playerColor === "w" ? "WHITE" : "BLACK"} in this position.` });
      }
      return;
    }
    if (selected.row === row && selected.col === col) {
      setSelected(null);
      setCandidates([]);
      return;
    }
    if (piece && ((playerColor === "w" && piece === piece.toUpperCase()) || (playerColor === "b" && piece === piece.toLowerCase()))) {
      const sq = squareName(row, col);
      const dests = legalDestinationsFrom(boardFen, sq);
      setSelected({ row, col });
      setCandidates(dests.map((d) => coordsFromSquare(d)));
      return;
    }
    const from = squareName(selected.row, selected.col);
    const to = squareName(row, col);
    const applied = tryMove(boardFen, { from, to });
    if (!applied) {
      toast.push({ kind: "danger", text: "Illegal move." });
      return;
    }
    const uciFull = applied.uci;
    const uci4 = uciFull.slice(0, 4);
    const accepted = item
      ? (item.best_moves ?? []).some((m) => m === uciFull || m.slice(0, 4) === uci4)
      : false;
    setSelected(null);
    setCandidates([]);
    if (accepted) {
      setResult("success");
      setBoardFen(applied.fen);
      toast.push({ kind: "success", text: `${applied.san}! Solved.` });
      if (item) {
        markSolved.mutate({
          itemId: item.id,
          outcome: {
            solved: true,
            tries_used: triesUsed + 1,
            ms_taken: startedAt ? Date.now() - startedAt : 0,
            wrong_moves: wrongAttempts,
            timed_out: false,
          },
        });
      }
      return;
    }
    // Wrong move: log it; do NOT reveal the correct move, do NOT lock the board.
    const nextTries = triesUsed + 1;
    setTriesUsed(nextTries);
    setWrongAttempts((w) => (w.includes(applied.san) ? w : [...w, applied.san]));

    if (isRoutine) {
      // Routine sessions follow the routine's tries policy strictly.
      if (maxTries != null && nextTries >= maxTries) {
        giveUp();
      }
      // Infinite tries (maxTries == null): keep going silently.
      return;
    }

    // Endless / analysis-fix sessions use the manual "on wrong move" switch.
    if (guessMode === "giveup") {
      giveUp();
    } else if (guessMode === "modal" && !modalDismissed) {
      setModalMove(applied.san);
    }
  }

  function nextPosition() {
    setHistory((h) => [
      ...h,
      {
        n: index + 1,
        ok: result === "success",
        label: result === "success" ? "Solved" : "Gave up",
        detail: wrongAttempts.length > 0 ? `${wrongAttempts.length} wrong` : `${triesUsed} tries`,
      },
    ]);
    if (index + 1 < items.length) {
      setIndex(index + 1);
    } else if (endless && sessionId) {
      // Endless: pull another random batch and keep going.
      refillEndless.mutate(sessionId, {
        onSuccess: (batch) => {
          if (batch.items.length === 0) {
            toast.push({ kind: "accent", text: "No more mistakes to drill — you're all caught up!" });
            nav(doneDestination);
            return;
          }
          setItems((prev) => [...prev, ...batch.items]);
          setIndex(index + 1);
        },
        onError: () => {
          toast.push({ kind: "danger", text: "Couldn't load more positions." });
          nav(doneDestination);
        },
      });
    } else {
      if (sessionId) completeSession.mutate(sessionId);
      toast.push({ kind: "accent", text: "Session complete — nice work." });
      nav(doneDestination);
    }
  }

  function giveUp(timedOut = false) {
    setGaveUp(true);
    setLineRevealed(true);
    setResult("fail");
    setModalMove(null);
    if (item) {
      markSolved.mutate({
        itemId: item.id,
        outcome: {
          solved: false,
          tries_used: triesUsed,
          ms_taken: startedAt ? Date.now() - startedAt : 0,
          wrong_moves: wrongAttempts,
          timed_out: timedOut,
        },
      });
    }
    if (timedOut) toast.push({ kind: "danger", text: "Time's up." });
  }

  function showHint() {
    setHint(true);
    toast.push({ kind: "accent", text: "Hint: focus on outposts and tactics in the centre." });
  }

  // Modal: "Continue" keeps the board live and suppresses the modal for this
  // position; "Give up" ends it.
  function modalContinue() {
    setModalMove(null);
    setModalDismissed(true);
  }
  function modalGiveUp() {
    setModalMove(null);
    giveUp();
  }

  const candidateSquares = hint && !result && correctTo ? [correctTo] : candidates;

  const LEFT_W = width >= 1280 ? 300 : width >= 1140 ? 260 : 230;
  const RIGHT_W = width >= 1280 ? 340 : width >= 1140 ? 300 : 260;
  const centerAvail = Math.max(280, width - LEFT_W - RIGHT_W - 80);
  const BOARD_DESKTOP = Math.min(540, Math.max(360, centerAvail));

  function MistakeBanner() {
    return (
      <div
        style={{
          background: p.danger,
          color: p.dangerFg,
          border: `2px solid ${p.border}`,
          boxShadow: offsetShadow(p, 4, 4),
          padding: isMobile ? 10 : 14,
          display: "flex",
          alignItems: "center",
          gap: isMobile ? 10 : 14,
        }}
      >
        <div
          style={{
            width: isMobile ? 32 : 42,
            height: isMobile ? 32 : 42,
            background: p.bg,
            color: p.fg,
            border: `2px solid ${p.bg}`,
            display: "grid",
            placeItems: "center",
            fontSize: isMobile ? 18 : 22,
            fontWeight: 800,
            flexShrink: 0,
          }}
        >
          !
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: isMobile ? 9.5 : 11, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", opacity: 0.85 }}>
            DRILL · {posLabel}
          </div>
          <div style={{ fontSize: isMobile ? 18 : 26, fontWeight: 800, fontFamily: TYPE.mono, letterSpacing: -0.4, lineHeight: 1.1, marginTop: 2 }}>
            {badMoveSan ? (
              <>
                You played <span style={{ background: p.bg, color: p.danger, padding: "0 7px", border: `2px solid ${p.bg}` }}>{badMoveSan}</span> — find better.
              </>
            ) : (
              "Find the better move."
            )}
          </div>
          {evalDropText && (
            <div style={{ fontSize: isMobile ? 11 : 13, fontFamily: TYPE.mono, fontWeight: 700, opacity: 0.85, marginTop: 3 }}>
              {evalDropText}
            </div>
          )}
        </div>
      </div>
    );
  }

  function ActionBar() {
    return (
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {!result ? (
          <>
            <BrBtn size="sm" variant="surface" onClick={showHint} disabled={hint} style={{ flex: isMobile ? 1 : "0 0 auto" }}>Hint</BrBtn>
            <BrBtn size="sm" variant="surface" onClick={() => setLineRevealed(true)} disabled={lineRevealed} style={{ flex: isMobile ? 1 : "0 0 auto" }}>Show Line</BrBtn>
            <BrBtn size="sm" variant="ghost" withShadow={false} onClick={() => giveUp()} style={{ flex: isMobile ? 1 : "0 0 auto" }}>Give Up</BrBtn>
            <div style={{ flex: 1 }} />
            {selected ? (
              <BrBtn size="md" variant="accent" onClick={() => { setSelected(null); setCandidates([]); }}>Cancel</BrBtn>
            ) : (
              <BrBtn size="md" variant="accent" disabled>Pick a piece</BrBtn>
            )}
          </>
        ) : (
          <>
            <BrBtn
              size="sm"
              variant="surface"
              onClick={() => {
                setResult(null);
                setSelected(null);
                setCandidates([]);
                setGaveUp(false);
                setTimeLeft(timeLimit ?? 0);
                setTriesUsed(0);
                setStartedAt(Date.now());
                setWrongAttempts([]);
                setModalMove(null);
                setModalDismissed(false);
                setLineRevealed(false);
                if (startFen) setBoardFen(startFen);
              }}
              style={{ flex: isMobile ? 1 : "0 0 auto" }}
            >
              Retry
            </BrBtn>
            <div style={{ flex: 1 }} />
            <BrBtn size="md" variant={result === "success" ? "success" : "accent"} onClick={nextPosition}>
              {endless || index + 1 < items.length
                ? "Next position →"
                : fromGameId
                  ? "Back to game →"
                  : "Done →"}
            </BrBtn>
          </>
        )}
      </div>
    );
  }

  function Timer() {
    // No per-move clock → no timer box (classical / no-limit / one-off sessions).
    if (timeLimit == null) return null;
    const fmt = (s: number) => `0:${String(Math.max(0, s)).padStart(2, "0")}`;
    const pct = Math.max(0, Math.min(1, timeLeft / timeLimit));
    const danger = timeLeft <= Math.max(3, Math.round(timeLimit * 0.25));
    return (
      <BrBox bg={p.accent} fg={p.accentFg} padding={isMobile ? 12 : 18} shadowSize={isMobile ? 3 : 4} label="CLOCK">
        <div style={{ fontSize: 10, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6, marginTop: 4 }}>TIME ON MOVE</div>
        <div
          style={{
            fontSize: isMobile ? 42 : 60,
            fontWeight: 800,
            fontFamily: TYPE.display,
            letterSpacing: -2,
            lineHeight: 0.9,
            color: danger ? p.danger : p.accentFg,
          }}
        >
          {fmt(timeLeft)}
        </div>
        <div style={{ fontSize: 11, fontFamily: TYPE.mono, color: p.accentFg, opacity: 0.7, marginTop: 4, fontWeight: 700 }}>OF {fmt(timeLimit)} ORIGINAL</div>
        <div style={{ marginTop: 10, height: 8, background: p.bg, border: `2px solid ${p.border}`, position: "relative", overflow: "hidden" }}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              width: `${pct * 100}%`,
              background: danger ? p.danger : p.fg,
              transition: "width 1s linear, background .25s",
            }}
          />
        </div>
      </BrBox>
    );
  }

  function ModeSwitch() {
    const opts: { key: "giveup" | "continue" | "modal"; label: string }[] = [
      { key: "modal", label: "Ask" },
      { key: "continue", label: "Keep guessing" },
      { key: "giveup", label: "Give up on miss" },
    ];
    return (
      <div>
        <BrLabel>ON WRONG MOVE</BrLabel>
        <div style={{ display: "flex", border: `2px solid ${p.border}`, boxShadow: offsetShadow(p, 2, 2), marginTop: 8 }}>
          {opts.map((o, i) => {
            const on = guessMode === o.key;
            return (
              <button
                key={o.key}
                onClick={() => changeGuessMode(o.key)}
                title={
                  o.key === "modal"
                    ? "Ask each time (Continue / Give up)"
                    : o.key === "continue"
                      ? "Always keep guessing"
                      : "Always give up after the first wrong guess"
                }
                style={{
                  flex: 1,
                  background: on ? p.accent : p.card,
                  color: on ? p.accentFg : p.fg,
                  border: "none",
                  borderLeft: i === 0 ? "none" : `2px solid ${p.border}`,
                  padding: "7px 6px",
                  fontFamily: TYPE.mono,
                  fontWeight: 800,
                  fontSize: 10,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                  cursor: "pointer",
                  lineHeight: 1.1,
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  function WrongAttempts() {
    if (wrongAttempts.length === 0) return null;
    return (
      <div
        style={{
          background: p.danger,
          color: p.dangerFg,
          border: `2px solid ${p.border}`,
          boxShadow: offsetShadow(p, 3, 3),
          padding: "10px 12px",
        }}
      >
        <div style={{ fontSize: 10, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", opacity: 0.85, marginBottom: 8 }}>
          WRONG MOVES · {wrongAttempts.length}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: isMobile ? 160 : 200, overflowY: "auto" }}>
          {wrongAttempts.map((m, i) => (
            <div
              key={`${m}-${i}`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                background: p.bg,
                color: p.fg,
                border: `2px solid ${p.bg}`,
                padding: "5px 10px",
                fontFamily: TYPE.mono,
                fontWeight: 800,
                fontSize: 13,
              }}
            >
              <span>{m}</span>
              <span style={{ color: p.danger, fontWeight: 800 }}>✕</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function WrongMoveModal() {
    if (!modalMove) return null;
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          display: "grid",
          placeItems: "center",
          zIndex: 1000,
          padding: 16,
        }}
        onClick={modalContinue}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: p.card,
            color: p.fg,
            border: `3px solid ${p.border}`,
            boxShadow: offsetShadow(p, 8, 8),
            padding: 24,
            maxWidth: 420,
            width: "100%",
          }}
        >
          <div style={{ fontSize: 11, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: p.danger, marginBottom: 8 }}>
            NOT CORRECT
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, fontFamily: TYPE.mono, letterSpacing: -0.4, lineHeight: 1.2 }}>
            <span style={{ color: p.danger }}>{modalMove}</span> was not correct. Do you want to keep trying?
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
            <BrBtn size="md" variant="accent" style={{ flex: 1 }} onClick={modalContinue}>
              Continue
            </BrBtn>
            <BrBtn size="md" variant="surface" style={{ flex: 1 }} onClick={modalGiveUp}>
              Give up
            </BrBtn>
          </div>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div style={{ minHeight: "100vh", background: p.bg, color: p.fg, fontFamily: TYPE.body, display: "flex", flexDirection: "column" }}>
        <nav
          style={{
            height: 56,
            padding: "0 14px",
            borderBottom: `2px solid ${p.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: p.bg,
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <BrBtn size="xs" variant="surface" onClick={() => nav(doneDestination)} withShadow={false} style={{ width: 38, height: 36, padding: 0, fontSize: 16 }}>
              ←
            </BrBtn>
            <div style={{ fontSize: 11, fontFamily: TYPE.mono, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
              <div>{endless ? "ENDLESS" : "TACTICS"}</div>
              <div style={{ color: p.fgMuted, marginTop: 1, fontSize: 10 }}>
                {endless ? `POS ${index + 1} · ${solvedCount} SOLVED` : `POS ${index + 1} / ${items.length}`}
              </div>
            </div>
          </div>
        </nav>
        <div style={{ padding: "12px 14px 8px", flexShrink: 0 }}>
          <MistakeBanner />
        </div>
        <div style={{ padding: "0 18px", display: "flex", justifyContent: "center", flexShrink: 0 }}>
          <ChessBoard
            palette={p}
            size={Math.min(354, window.innerWidth - 36)}
            position={board}
            selected={selected}
            onSquareClick={handleSquareClick}
            candidateSquares={candidateSquares}
            arrowFrom={result ? null : badArrowFrom}
            arrowTo={result ? null : badArrowTo}
            flipped={playerColor === "b"}
            animMs={70}
          />
        </div>
        <div style={{ padding: "10px 14px", flexShrink: 0 }}>
          <ActionBar />
        </div>
        <div style={{ padding: "4px 14px 18px", display: "flex", flexDirection: "column", gap: 14, overflowY: "auto" }}>
          <WrongAttempts />
          {!isRoutine && <ModeSwitch />}
        </div>
        <WrongMoveModal />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: p.bg, color: p.fg, fontFamily: TYPE.body, display: "flex", flexDirection: "column" }}>
      <nav
        style={{
          height: 64,
          padding: "0 28px",
          borderBottom: `2px solid ${p.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: p.bg,
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          <button
            onClick={() => nav("/app/train")}
            style={{
              background: "transparent",
              border: "none",
              color: p.fg,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 18,
              fontWeight: 800,
              letterSpacing: -0.5,
              fontFamily: TYPE.display,
            }}
          >
            <Logo size={32} />
            GAMBIT.
          </button>
          <div style={{ borderLeft: `2px solid ${p.border}`, paddingLeft: 24, fontSize: 12, fontFamily: TYPE.mono, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, color: p.fgMuted }}>
            {endless ? "ENDLESS" : "TRAINING"} / {posLabelPad}
          </div>
        </div>
      </nav>

      <div style={{ flex: 1, display: "grid", gridTemplateColumns: `${LEFT_W}px 1fr ${RIGHT_W}px`, overflow: "hidden", minHeight: 0 }}>
        <aside style={{ borderRight: `2px solid ${p.border}`, padding: "22px 18px", background: p.bgAlt, display: "flex", flexDirection: "column", gap: 18, overflow: "hidden" }}>
          <div>
            <BrLabel>SESSION</BrLabel>
            <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.5, textTransform: "uppercase", marginTop: 6, lineHeight: 1.05 }}>
              {endless ? <>ENDLESS<br />DRILL</> : <>YOUR BLUNDERS<br />REDRILLED</>}
            </div>
            <div style={{ fontSize: 12, color: p.fgMuted, marginTop: 8, fontFamily: TYPE.mono, letterSpacing: 0.5 }}>
              {endless ? `${solvedCount} SOLVED · KEEP GOING` : `${items.length} POS · ~${session?.target_minutes ?? 0} MIN`}
            </div>
            <div style={{ marginTop: 14, height: 16, background: p.bg, border: `2px solid ${p.border}`, position: "relative" }}>
              {endless ? (
                // No bounded total in endless mode — show solved count, not a bar.
                <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", fontSize: 9.5, fontWeight: 800, fontFamily: TYPE.mono, color: p.fg, letterSpacing: 1 }}>
                  {solvedCount} SOLVED
                </div>
              ) : (
                <>
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: `${(index / Math.max(1, items.length)) * 100}%`,
                      background: p.accent,
                      borderRight: `2px solid ${p.border}`,
                      transition: "width .4s",
                    }}
                  />
                  <div style={{ position: "absolute", top: "50%", right: 6, transform: "translateY(-50%)", fontSize: 9.5, fontWeight: 800, fontFamily: TYPE.mono, color: p.fg }}>
                    {index}/{items.length}
                  </div>
                </>
              )}
            </div>
          </div>
          <div style={{ borderTop: `2px solid ${p.border}`, paddingTop: 14, flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <BrLabel>SESSION LOG</BrLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, overflowY: "auto", marginTop: 10, paddingRight: 2 }}>
              {[...history].reverse().map((h) => (
                <div
                  key={h.n}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    background: p.card,
                    border: `2px solid ${p.border}`,
                    boxShadow: offsetShadow(p, 2, 2),
                    fontSize: 11.5,
                    fontFamily: TYPE.mono,
                    fontWeight: 700,
                  }}
                >
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      background: h.ok ? p.success : p.danger,
                      color: h.ok ? p.successFg : p.dangerFg,
                      border: `2px solid ${p.border}`,
                      display: "grid",
                      placeItems: "center",
                      fontSize: 11,
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    {h.ok ? "✓" : "✕"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textTransform: "uppercase" }}>{h.label}</div>
                    <div style={{ fontSize: 9.5, color: p.fgMuted, marginTop: 1, letterSpacing: 0.5, textTransform: "uppercase" }}>{h.detail}</div>
                  </div>
                  <div style={{ fontSize: 10, color: p.fgMuted }}>#{h.n}</div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        <main style={{ padding: "22px 32px", display: "flex", flexDirection: "column", alignItems: "center", overflow: "auto", minWidth: 0 }}>
          <div style={{ width: "100%", maxWidth: BOARD_DESKTOP + 60, marginBottom: 18 }}>
            <MistakeBanner />
          </div>
          <ChessBoard palette={p} size={BOARD_DESKTOP} position={board} selected={selected} onSquareClick={handleSquareClick} candidateSquares={candidateSquares} arrowFrom={result ? null : badArrowFrom} arrowTo={result ? null : badArrowTo} flipped={playerColor === "b"} animMs={70} />
          <div style={{ width: BOARD_DESKTOP, marginTop: 16 }}>
            <ActionBar />
          </div>
        </main>

        <aside style={{ borderLeft: `2px solid ${p.border}`, padding: "22px 18px", background: p.bgAlt, display: "flex", flexDirection: "column", gap: 18, overflow: "hidden" }}>
          <Timer />
          <BrBox padding={14} shadowSize={4} label="CONTEXT">
            <div style={{ display: "flex", flexDirection: "column", gap: 9, fontSize: 12, fontFamily: TYPE.mono, fontWeight: 700, marginTop: 4 }}>
              <Row k="POSITION" v={endless ? `#${index + 1}` : `${index + 1} / ${items.length}`} muted={p.fgMuted} />
              <Row k="SIDE" v={playerColor === "w" ? "WHITE" : "BLACK"} muted={p.fgMuted} />
              <Row k="FORMAT" v={session?.format?.toUpperCase() ?? "TACTICS"} muted={p.fgMuted} />
              <Row
                k={bestLinesSan.length > 1 ? "BEST LINES" : "BEST LINE"}
                v={
                  lineRevealed ? (
                    bestLinesSan.length > 0 ? (
                      <span style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "flex-end" }}>
                        {bestLinesSan.map((line, i) => (
                          <BrTag key={i} bg={p.success} fg={p.successFg}>{line}</BrTag>
                        ))}
                      </span>
                    ) : (
                      <BrTag bg={p.success} fg={p.successFg}>—</BrTag>
                    )
                  ) : (
                    <span style={{ color: p.fgMuted }}>—</span>
                  )
                }
                muted={p.fgMuted}
              />
            </div>
          </BrBox>
          <WrongAttempts />
          {!isRoutine && <ModeSwitch />}
        </aside>
      </div>
      <WrongMoveModal />
    </div>
  );
}

function Row({ k, v, muted }: { k: string; v: React.ReactNode; muted: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
      <span style={{ color: muted, letterSpacing: 1 }}>{k}</span>
      <span>{v}</span>
    </div>
  );
}

function Centered({ children, p }: { children: React.ReactNode; p: ReturnType<typeof useTheme> }) {
  return (
    <div style={{ minHeight: "100vh", background: p.bg, color: p.fg, display: "grid", placeItems: "center", padding: 16, fontFamily: TYPE.body, fontWeight: 700, textAlign: "center" }}>
      <div>{children}</div>
    </div>
  );
}
